import _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import CustomModal from "shared/components/CustomModal";
import CustomPopover from "shared/components/Popover";
import { PageLoadSpinner } from "../../shared/components/PageLoadSpinner";
import { HashMap } from "../../shared/model/HashMap";
import { FixedOption } from "../../shared/model/Option";
import { Utilities } from "../../shared/utilities";
import { MultiSelect } from "shared/components/select/Select";

const EnvironmentVariableExpander: React.FC<IEnvironmentVariableExpanderProps> = (props) => {

    const environmentVariableRegExp = /(#(env)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\()){1,2})/ig
    const parameterInputValue = props.parameterInputValue?.toString();

    const [showEnvironmentVariableValuesModal, setShowEnvironmentVariableValuesModal] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const [environmentVariables, setEnvironmentVariables] = React.useState<[string, HashMap<string>][]>();
    const [allEnvironmentSelectors, setAllEnvironmentSelectors] = React.useState<FixedOption[]>([]);
    const [currentEnvironmentSelectors, setCurrentEnvironmentSelectors] = React.useState<FixedOption[]>();
    const [environmentHeaders, setEnvironmentHeaders] = React.useState<string[]>([]);

    const getAvailableEnvironmentVariables = () => {
        const environmentVariables: string[] = []
        let match: RegExpExecArray | null;
        while (match = environmentVariableRegExp.exec(parameterInputValue)) {
            const refString = match[1];
            const refWithoutPrefix = refString.substr(refString.indexOf(".") + 1);
            environmentVariables.push(refWithoutPrefix)
        }
        return environmentVariables;
    }

    const getEnvValues = async () => {
        try {
            const envVars = getAvailableEnvironmentVariables();
            if (!_.isEmpty(envVars)) {
                const envVarValues: HashMap<HashMap<string>> = await Utilities.getFetch(`api/environmentVariable/getSchemaValuesByName`, { variables: envVars })
                return envVarValues;
            }
        } catch (e) {
            Utilities.toastError(e);
            console.error(e)
        }
    }

    React.useEffect(() => {
        if (showEnvironmentVariableValuesModal) {
            const fetch = async () => {
                try {
                    setIsLoading(true);
                    const environmentVariableValues = await getEnvValues();
                    const variables = environmentVariableValues && Object.entries(environmentVariableValues);
                    const headers = _.uniq(_.flatten(variables?.map((x) => Object.keys(x[1]))))
                    const selectors: FixedOption[] = headers?.map((x, i) => ({ value: x, label: x, isFixed: i !== 0 ? false : true }))
                    setEnvironmentVariables(variables);
                    setEnvironmentHeaders(headers);
                    setAllEnvironmentSelectors(selectors)
                    setCurrentEnvironmentSelectors(selectors.filter((x) => x.isFixed))
                    setIsLoading(false);
                } catch (e) {
                    setIsLoading(false);
                    Utilities.toastError(e);
                    console.error(e);
                }
            }
            fetch();
        }
    }, [showEnvironmentVariableValuesModal])


    const renderEnvironmentVariableValuesModal = () => {
        const onHideHandler = () => setShowEnvironmentVariableValuesModal(false);

        const orderOptions = (values: FixedOption[]): FixedOption[] => {
            return values.filter(v => v.isFixed).concat(values.filter(v => !v.isFixed));
        };

        const onEnvChange = (value: FixedOption[], { action, removedValue }: { action: any, removedValue: FixedOption }) => {
            switch (action) {
                case 'remove-value':
                case 'pop-value':
                    if (removedValue.isFixed) {
                        return;
                    }
                    break;
                case 'clear':
                    value = allEnvironmentSelectors.filter(v => v.isFixed);
                    break;
            }

            value = orderOptions(value);
            setCurrentEnvironmentSelectors(value);
        }

        const whereCurrentEnvironmentSelectorsApply = (f: string) => currentEnvironmentSelectors?.some((s) => s.value.toLowerCase() === f.toLowerCase());

        if (isLoading) {
            return <PageLoadSpinner />
        }

        const environmentVariableHeaders = environmentHeaders.filter((f) => whereCurrentEnvironmentSelectorsApply(f));

        return (
            <CustomModal
                modalTitle={<h2>{FRENDS.Localizations.EnvironmentVariables}</h2>}
                show={true}
                label="environment-variable-values"
                size="large"
                onClose={onHideHandler}
                shouldCloseOnOverlayClick={true}
            >
                {_.isEmpty(environmentVariables) ? (
                    <h4>No Environment variable values were fetched. Entire Environment variable groups are not fetched.</h4>
                ) : (
                    <S.FormGroup>
                        <S.FormGroup>
                            <MultiSelect
                                value={Utilities.valueFromListWithFixed(allEnvironmentSelectors, currentEnvironmentSelectors?.map((x) => x.value))}
                                options={allEnvironmentSelectors}
                                isClearable={allEnvironmentSelectors.some(v => !v.isFixed)}
                                onChange={onEnvChange}
                                menuPortalTarget={document.body}
                            />
                        </S.FormGroup>
                        <S.FormGroup>
                            <S.StyledTable columnCount={environmentVariableHeaders.length + 1}>
                                <thead>
                                    <tr>
                                        <th>Environment variable</th>
                                        {environmentVariableHeaders.map((environment, idnx) => (
                                            <th key={idnx}>Value in {environment}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {environmentVariables?.map((variable, idx) => {
                                        const valueInEnvironments = Object.entries(variable[1])
                                        const environmentVariableName = variable[0];
                                        return (
                                            <tr key={idx} style={{ border: "none" }}>
                                                <td><b>{environmentVariableName}</b></td>
                                                {valueInEnvironments.filter((f) => whereCurrentEnvironmentSelectorsApply(f[0])).map((environmentValue, idnx) => {
                                                    let value = environmentValue[1];
                                                    const valueWithTooltip = () => (
                                                        <CustomPopover
                                                            tooltip={value}
                                                        >
                                                            <span>{_.truncate(value, { length: 25 })}</span>
                                                        </CustomPopover>
                                                    );
                                                    if (_.isArray(value)) {
                                                        // Show Environment variable list as an array
                                                        value = JSON.stringify(value)
                                                    }
                                                    return <td className={`value-in-${environmentValue[0]}`} key={idnx}>{value.trim().length < 25 ? value : valueWithTooltip()}</td>
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </S.StyledTable>
                        </S.FormGroup>
                    </S.FormGroup>
                )}
            </CustomModal>
        )
    }

    const handleShowEnvironmentVariableValuesModal = () => setShowEnvironmentVariableValuesModal(show => !show);

    const toolTip = "View associated Environment variable values";

    if (parameterInputValue === null) {
        return null;
    }

    return (
        <>
            {!_.isEmpty(getAvailableEnvironmentVariables()) ? (
                <span title={toolTip} className="pointer expand-environment-variables" onClick={handleShowEnvironmentVariableValuesModal}>
                    &nbsp;<i className={`ci-list_ul description-tooltip-icon-color`} style={{ visibility: props.shouldDisplayIcon ? "visible" : "hidden" }} />
                </span>
            ) : null}
            {showEnvironmentVariableValuesModal && renderEnvironmentVariableValuesModal()}
        </>
    )

}

interface IEnvironmentVariableExpanderProps {
    parameterInputValue: string | number | boolean;
    shouldDisplayIcon: boolean;
}

export default EnvironmentVariableExpander;